import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function SubscriptionSuccessPage() {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<"verifying" | "success" | "error">("verifying");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setStatus("error");
      setErrorMsg("No session ID found. Please contact support.");
      return;
    }

    // Call verify-session to activate subscription immediately
    fetch(`/api/billing/verify-session?session_id=${encodeURIComponent(sessionId)}`, {
      credentials: "include",
    })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          if (data.paid) {
            setStatus("success");
            // Invalidate all subscription/billing queries so the UI updates
            queryClient.invalidateQueries({ queryKey: ["/api/subscriptions/usage"] });
            queryClient.invalidateQueries({ queryKey: ["/api/user"] });
            queryClient.invalidateQueries({ queryKey: ["/api/billing/subscription"] });
            queryClient.invalidateQueries({ queryKey: ["/api/businesses"] });
            // Auto-redirect to dashboard after 3 seconds
            setTimeout(() => navigate("/dashboard"), 3000);
          } else {
            setStatus("error");
            setErrorMsg("Payment was not completed. Please try again.");
          }
        } else {
          const err = await res.json().catch(() => ({ message: "Verification failed" }));
          setStatus("error");
          setErrorMsg(err.message || "Could not verify your subscription.");
        }
      })
      .catch(() => {
        setStatus("error");
        setErrorMsg("Network error. Please refresh the page.");
      });
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-8 text-center space-y-4">
          {status === "verifying" && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
              <h2 className="text-xl font-semibold">Activating your subscription...</h2>
              <p className="text-muted-foreground text-sm">Please wait while we confirm your payment.</p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto" />
              <h2 className="text-xl font-semibold">Subscription activated!</h2>
              <p className="text-muted-foreground text-sm">
                Your ZentroBooks subscription is now active. Redirecting to your dashboard...
              </p>
              <Button onClick={() => navigate("/dashboard")} className="mt-4">
                Go to Dashboard
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <XCircle className="h-12 w-12 text-red-600 mx-auto" />
              <h2 className="text-xl font-semibold">Something went wrong</h2>
              <p className="text-muted-foreground text-sm">{errorMsg}</p>
              <div className="flex gap-2 justify-center mt-4">
                <Button variant="outline" onClick={() => navigate("/subscription")}>
                  Try Again
                </Button>
                <Button onClick={() => navigate("/dashboard")}>
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
